import type { Insurance } from '@physiofit-digital/physiofit_types';
import { useMemo, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { useInsuranceCatalog } from '../../hooks/useInsuranceCatalog';
import { insuranceListName, searchInsurances } from '../../insurance/searchInsurances';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, space, text } from '../../../../styles/onboardingUiTokens';
import { LoadingSpinner } from '../elements/LoadingSpinner';
import { SvgIcon } from '../icons/SvgIcon';
import { Body } from '../typography/Body';
import { formControlSurfaceCss, VisuallyHidden } from '../forms/formFieldLayout';

/** Row avatar size as drawn (Figma `26603:12224`, ERS-61 A5). */
const ROW_LOGO_PX = 30;

/** Overview row label for all regional AOK insurers (click filters search to sub-options). */
const AOK_GROUP_DISPLAY_LABEL = 'AOK';

/** Cloudflare Images delivery URL variant for web `<img>`. */
function insuranceLogoSrc(url?: string): string | undefined {
  const u = url?.trim();
  if (!u) {
    return undefined;
  }
  if (!u.includes('/imagedelivery')) {
    return u;
  }
  const withoutPublic = u.replace('/public', '');
  return `${withoutPublic}/w=400`;
}

const LIST_VIEWPORT_MIN = 'min(42vh, 22rem)';
const LIST_VIEWPORT_MAX = 'min(55vh, 28rem)';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-height: 0;
  flex: 1;
  box-sizing: border-box;
  gap: ${space.sm};
`;

const TitleRow = styled.div`
  padding: 0;
`;

const SearchBlock = styled.div`
  padding: 0;
  margin-bottom: ${space.md};
`;

const SearchShell = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  ${formControlSurfaceCss}
  margin: 0;
  /* room for the trailing magnifier — must come after the shared surface's padding */
  padding-right: calc(${space.md} + 30px);
`;

const SearchIconWrap = styled.span`
  position: absolute;
  right: ${space.md};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space.sm};
  padding: ${space.md} 0;
  min-height: ${LIST_VIEWPORT_MIN};
`;

const ErrorText = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  color: ${colors.lightRed};
`;

const RetryButton = styled.button`
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${colors.primary};
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${colors.dark};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ChoiceRowList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 ${space.sm};
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
  width: 100%;
  box-sizing: border-box;
`;

const ChoiceRowButton = styled.button`
  appearance: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  /* visual row is 32px (as drawn); min-height keeps the hit area >= 44px (G8) */
  min-height: 44px;
  padding: 2px 5px 2px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: ${getMediumRGBA(0.07)};
    }
  }

  &:active:not(:disabled) {
    background: ${getMediumRGBA(0.11)};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ChoiceRowAvatar = styled.span`
  flex: 0 0 auto;
  width: ${ROW_LOGO_PX}px;
  height: ${ROW_LOGO_PX}px;
  border-radius: 50%;
  overflow: hidden;
  background: ${getMediumRGBA(0.06)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ChoiceRowAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const ChoiceRowAvatarFallback = styled.span`
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.caption.fontSize};
  font-weight: 700;
  color: ${colors.dark};
  text-align: center;
  line-height: 1.1;
  padding: 0 ${space.xxxs};
`;

const ChoiceRowLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 500;
  color: ${colors.medium};
`;

const ChoiceRowArrow = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: ${space.xs};
  opacity: 0.75;
`;

const ListWrap = styled.div`
  flex: 1 1 auto;
  min-height: ${LIST_VIEWPORT_MIN};
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ListScroll = styled(ChoiceRowList)`
  padding: ${space.xs} 0 ${space.lg};
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  max-height: ${LIST_VIEWPORT_MAX};
`;

const EmptyResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.md} 0;
`;

const EmptyHint = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  color: ${colors.medium};
`;

const ListPlaceholder = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
`;

export type InsuranceSelectProps = Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  onSelect: (insurance: Insurance) => void;
  /** Screen title above search; omit or leave empty when the parent screen provides its own heading. */
  title?: string;
  searchPlaceholder?: string;
  /** Accessible name for the search field (input is visually placeholder-led). */
  searchLabel?: string;
  disabled?: boolean;
  /** Seeds the search field on mount (e.g. persisted insurer name in read-only mode). */
  initialSearchValue?: string;
  /** When true, only the search shell is shown; favorites and results are hidden. */
  hideChoiceList?: boolean;
  /** Rendered under the hint when a search finds no insurer. */
  emptyResultAction?: ReactNode;
};

function firstAokInsurance(insurances: Insurance[]): Insurance | undefined {
  return insurances.find((i) => /aok/i.test(i.name));
}

type InsuranceChoiceRowProps = {
  shown: string;
  imageSrc?: string;
  /** Shown inside the avatar circle when there is no logo image. */
  avatarFallback?: string;
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
};

function InsuranceChoiceRow({
  shown,
  imageSrc,
  avatarFallback,
  onClick,
  disabled,
  ariaLabel,
}: InsuranceChoiceRowProps) {
  const fb = avatarFallback ?? shown.slice(0, 3);
  return (
    <ChoiceRowButton type="button" disabled={disabled} onClick={onClick} aria-label={ariaLabel}>
      <ChoiceRowAvatar aria-hidden>
        {imageSrc ? (
          <ChoiceRowAvatarImg src={imageSrc} alt="" decoding="async" />
        ) : (
          <ChoiceRowAvatarFallback>{fb}</ChoiceRowAvatarFallback>
        )}
      </ChoiceRowAvatar>
      <ChoiceRowLabel>{shown}</ChoiceRowLabel>
      <ChoiceRowArrow aria-hidden>
        <SvgIcon name="arrowright" size={24} primaryColor={colors.medium} />
      </ChoiceRowArrow>
    </ChoiceRowButton>
  );
}

export function InsuranceSelect({
  onSelect,
  title = 'Finde deine Krankenversicherung',
  searchPlaceholder = 'Deine Krankenversicherung',
  searchLabel = 'Krankenkasse suchen',
  disabled = false,
  initialSearchValue = '',
  hideChoiceList = false,
  emptyResultAction,
  className,
  ...rest
}: InsuranceSelectProps) {
  const { insurances, loading, error, reload } = useInsuranceCatalog();
  const [filterText, setFilterText] = useState(initialSearchValue);
  const queryActive = filterText.trim().length >= 1;

  const filteredInsurances = useMemo(() => {
    if (!queryActive) {
      return [];
    }
    return searchInsurances(insurances, filterText);
  }, [insurances, filterText, queryActive]);

  const favoriteInsurances = useMemo(
    () =>
      [...insurances.filter((i) => i.isFavorite)].sort(
        (a, b) => (b.filterIndex ?? 0) - (a.filterIndex ?? 0) || a.name.localeCompare(b.name, 'de'),
      ),
    [insurances],
  );

  const aokSample = useMemo(() => firstAokInsurance(insurances), [insurances]);
  const aokLogoSrc = aokSample ? insuranceLogoSrc(aokSample.logo?.assetUrl) : undefined;

  const searchId = 'insurance-select-search';

  const showTitle = Boolean(title?.trim());

  return (
    <Root className={className} {...rest}>
      {showTitle ? (
        <TitleRow>
          <Body variant="document" size="mini" tone="muted">
            {title}
          </Body>
        </TitleRow>
      ) : null}

      <SearchBlock>
        <VisuallyHidden>
          <label htmlFor={searchId}>{searchLabel}</label>
        </VisuallyHidden>
        <SearchShell>
          <SearchInput
            id={searchId}
            type="search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder={searchPlaceholder}
            disabled={disabled || loading || Boolean(error)}
            autoComplete="off"
            enterKeyHint="search"
            spellCheck={false}
          />
          <SearchIconWrap aria-hidden>
            <SvgIcon name="search" size={22} primaryColor={colors.medium} />
          </SearchIconWrap>
        </SearchShell>
      </SearchBlock>

      {hideChoiceList ? null : loading ? (
        <Centered>
          <LoadingSpinner color={colors.primary} size={32} />
        </Centered>
      ) : error ? (
        <Centered>
          <ErrorText>{error}</ErrorText>
          <RetryButton type="button" onClick={() => void reload()} disabled={disabled}>
            Erneut versuchen
          </RetryButton>
        </Centered>
      ) : (
        <>
          {!queryActive ? (
            <div role="region" aria-label="Häufige Krankenkassen">
              <ChoiceRowList>
                <li key="aok-filter">
                  <InsuranceChoiceRow
                    shown={AOK_GROUP_DISPLAY_LABEL}
                    imageSrc={aokLogoSrc}
                    avatarFallback={AOK_GROUP_DISPLAY_LABEL}
                    onClick={() => setFilterText(AOK_GROUP_DISPLAY_LABEL)}
                    disabled={disabled}
                    ariaLabel="Nach AOK filtern"
                  />
                </li>
                {favoriteInsurances.map((insurance) => {
                  const src = insuranceLogoSrc(insurance.logo?.assetUrl);
                  const shown = insuranceListName(insurance);
                  return (
                    <li key={insurance.id}>
                      <InsuranceChoiceRow
                        shown={shown}
                        imageSrc={src}
                        onClick={() => onSelect(insurance)}
                        disabled={disabled}
                        ariaLabel={shown}
                      />
                    </li>
                  );
                })}
              </ChoiceRowList>
            </div>
          ) : null}

          {queryActive ? (
            <ListWrap role="region" aria-label="Krankenkassen">
              {filteredInsurances.length === 0 ? (
                <EmptyResult>
                  <EmptyHint>Keine Krankenkasse gefunden. Passe die Suche an.</EmptyHint>
                  {emptyResultAction}
                </EmptyResult>
              ) : (
                <ListScroll>
                  {filteredInsurances.map((insurance) => {
                    const src = insuranceLogoSrc(insurance.logo?.assetUrl);
                    const shown = insuranceListName(insurance);
                    return (
                      <li key={insurance.id}>
                        <InsuranceChoiceRow
                          shown={shown}
                          imageSrc={src}
                          onClick={() => onSelect(insurance)}
                          disabled={disabled}
                          ariaLabel={shown}
                        />
                      </li>
                    );
                  })}
                </ListScroll>
              )}
            </ListWrap>
          ) : (
            <ListPlaceholder aria-hidden />
          )}
        </>
      )}
    </Root>
  );
}
